import { type FC, useEffect, useRef, useState } from "react";

type IconShipAnimatedProps = {
    size?: number;
    className?: string;
    /** When true, play the bob + wave animation. Controlled by the parent. */
    animate?: boolean;
    /** When true, loop the animation indefinitely (e.g. new changelog). */
    continuous?: boolean;
};

/**
 * Ship icon with CSS-based bob animation. Uses a key counter to force
 * React to remount the animated group on each hover, which reliably
 * restarts CSS animations. SMIL was dropped because browsers don't
 * reliably restart SMIL animations on re-injection.
 */
export const IconShipAnimated: FC<IconShipAnimatedProps> = ({
    size = 20,
    className,
    animate = false,
    continuous = false,
}) => {
    const [key, setKey] = useState(0);
    const prevAnimate = useRef(animate);

    // Increment key each time animate transitions from false to true,
    // forcing React to remount the animated elements and restart CSS animations.
    useEffect(() => {
        if (animate && !prevAnimate.current) {
            setKey((k) => k + 1);
        }
        prevAnimate.current = animate;
    }, [animate]);

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <style>
                {`
                    @keyframes ship-bob {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-1px); }
                    }
                    @keyframes wave-morph {
                        0%, 100% {
                            d: path("M1.66663 13.6666 L4.07229 13.1602 L4.69595 13.0289 C4.78652 13.0098 4.88007 13.0098 4.97063 13.0289 L7.86262 13.6377 C7.95319 13.6568 8.04673 13.6568 8.1373 13.6377 L11.0293 13.0289 C11.1199 13.0098 11.2134 13.0098 11.304 13.0289 L11.9276 13.1602 L14.3333 13.6666");
                        }
                        50% {
                            d: path("M1.66663 13.6666 L4.07229 13.6377 L4.69595 13.6377 C4.78652 13.6568 4.88007 13.6568 4.97063 13.6377 L7.86262 13.0289 C7.95319 13.0098 8.04673 13.0098 8.1373 13.0289 L11.0293 13.6377 C11.1199 13.6568 11.2134 13.6568 11.304 13.6377 L11.9276 13.6377 L14.3333 13.6666");
                        }
                    }
                    .ship-hull-animated {
                        animation: ship-bob 1.6s ease-in-out ${continuous ? "infinite" : "3"};
                    }
                    .ship-wave-animated {
                        animation: wave-morph 1.6s ease-in-out ${continuous ? "infinite" : "3"};
                    }
                `}
            </style>

            {/* Ship hull + mast — bobs vertically when animated */}
            <g key={animate ? key : "static"} className={animate ? "ship-hull-animated" : undefined}>
                <path
                    d="M3.66663 7.66663V4.33329H12.3333V7.66663M6.33329 4.33329V1.66663H9.66663V4.33329"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M4.07229 13.1602C3.11359 11.9139 2.68871 10.3628 2.45732 8.92924C2.40281 8.59154 2.62356 8.27332 2.95643 8.19448L7.84632 7.03635C7.94735 7.01242 8.05257 7.01242 8.1536 7.03635L13.0435 8.19448C13.3764 8.27332 13.5971 8.59154 13.5426 8.92924C13.3112 10.3628 12.8863 11.9139 11.9276 13.1602"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>

            {/* Water waves — morphs path when animated */}
            <path
                key={animate ? `wave-${key}` : "wave-static"}
                className={animate ? "ship-wave-animated" : undefined}
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M1.66663 13.6666 L4.07229 13.1602 L4.69595 13.0289 C4.78652 13.0098 4.88007 13.0098 4.97063 13.0289 L7.86262 13.6377 C7.95319 13.6568 8.04673 13.6568 8.1373 13.6377 L11.0293 13.0289 C11.1199 13.0098 11.2134 13.0098 11.304 13.0289 L11.9276 13.1602 L14.3333 13.6666"
            />
        </svg>
    );
};
